import React, { useCallback, useEffect, useRef, useState } from "react";
import type { RawFileUsage, PathTypeSchema, StorageDeletionPreview } from "@domain/schemas/BI";
import { DeleteFileDialog } from "./DeleteFileDialog";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import axios from "axios";

interface DeleteTarget {
  projectId: string;
  /**
  Full path of the item relative to the selected storage root, e.g. `project-a/sample.raw`.
  */
  id: string;
  fileName: string;
  folder: string;
  isFolder: boolean;
  sourcePath?: string | null;
}

interface UseDeleteFileDialogOptions {
  projectId: string | null | undefined;
  /**
  Invoked after a successful deletion so the caller can refresh listings and prune selection state.
  */
  onDeleted: (deleted: DeleteTarget) => void | Promise<void>;
  deleteFile: (target: DeleteTarget) => Promise<boolean>;
  getUsage?: (target: DeleteTarget) => Promise<RawFileUsage>;
  pathType: PathTypeSchema;
}

/**
 * Owns shared explorer deletion: recursive folder preview, usage checks, confirmation and error surfacing.
 *
 * Extracted from FileExplorerGrid, which is already large, following the existing
 * `useArtifactDownloadDialog` / `useFailedBatchesDialog` convention.
 */
export const useDeleteFileDialog = ({ projectId, onDeleted, deleteFile, getUsage, pathType }: UseDeleteFileDialogOptions) => {
  const requestHandler = useInjection<IAuthenticatedRequestHandlerService>(TYPES.IAuthenticatedRequestHandlerService);
  const [target, setTarget] = useState<DeleteTarget | null>(null);
  const [usage, setUsage] = useState<RawFileUsage | undefined>(undefined);
  const [isLoadingUsage, setIsLoadingUsage] = useState(false);
  const [usageLookupFailed, setUsageLookupFailed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deletingRef = useRef(false);
  const [preview, setPreview] = useState<StorageDeletionPreview | null>(null);

  /**
  Identifies the dialog instance that owns the in-flight usage lookup.

  The lookup is asynchronous, so without this a request started for file A can resolve after the user has
  cancelled and opened file B, overwriting B's warning with A's result and enabling B's Delete button on the
  strength of a check that was never run against B. Opening or closing the dialog invalidates any pending
  request by advancing the counter; a resolved request only applies its outcome while it is still current.
  */
  const lookupGenerationRef = useRef(0);

  const closeDialog = useCallback(() => {
    lookupGenerationRef.current += 1;
    setTarget(null);
    setUsage(undefined);
    setIsLoadingUsage(false);
    setUsageLookupFailed(false);
    setIsDeleting(false);
    setDeleteError(null);
    deletingRef.current = false;
    setPreview(null);
  }, []);

  useEffect(() => {
    closeDialog();
    return () => {
      lookupGenerationRef.current += 1;
    };
  }, [projectId, closeDialog]);

  const openDeleteDialog = useCallback(
    async (nextTarget: DeleteTarget) => {
      const generation = ++lookupGenerationRef.current;

      setTarget(nextTarget);
      setUsage(undefined);
      setUsageLookupFailed(false);
      setIsDeleting(false);
      setDeleteError(null);
      setPreview(null);

      setIsLoadingUsage(Boolean(getUsage) || nextTarget.isFolder);
      if (!getUsage && !nextTarget.isFolder) return;

      try {
        let result: RawFileUsage | undefined;
        if (nextTarget.isFolder) {
          const folderPreview = await requestHandler.handleRequestAsync(() =>
            axios.get<StorageDeletionPreview>(`${runtimeConfig.API_BASE_URL}/projects/${encodeURIComponent(nextTarget.projectId)}/storage-folders/deletion-preview`, {
              params: { pathType, fileName: nextTarget.fileName, folder: nextTarget.folder || undefined, rootPath: nextTarget.sourcePath ?? undefined },
            }),
          );
          if (lookupGenerationRef.current !== generation) return;
          if (!Array.isArray(folderPreview.files) || !folderPreview.version || !folderPreview.usage) {
            throw new Error("The folder deletion preview is incomplete.");
          }
          setPreview(folderPreview);
          result = folderPreview.usage;
        } else {
          result = await getUsage?.(nextTarget);
        }
        if (lookupGenerationRef.current !== generation) {
          return;
        }
        setUsage(result);
      } catch (error) {
        // A failed lookup must not imply the file is unused, so the dialog says so explicitly.
        console.error("Failed to load file deletion checks:", error);
        if (lookupGenerationRef.current !== generation) {
          return;
        }
        setUsageLookupFailed(true);
      } finally {
        if (lookupGenerationRef.current === generation) {
          setIsLoadingUsage(false);
        }
      }
    },
    [getUsage, pathType, requestHandler],
  );

  const confirmDelete = useCallback(async () => {
    if (!target || isLoadingUsage || usageLookupFailed || deletingRef.current || target.projectId !== projectId) {
      return;
    }
    if (target.isFolder && (!preview?.version || !preview.files || !preview.usage)) {
      return;
    }
    if (!usage?.canDelete && (getUsage || target.isFolder)) {
      return;
    }

    setIsDeleting(true);
    deletingRef.current = true;
    setDeleteError(null);
    const generation = lookupGenerationRef.current;
    try {
      const deleted = target.isFolder
        ? await requestHandler.handleRequestAsync(() =>
            axios.delete<boolean>(`${runtimeConfig.API_BASE_URL}/projects/${encodeURIComponent(target.projectId)}/storage-folders`, {
              params: { pathType, fileName: target.fileName, folder: target.folder || undefined, rootPath: target.sourcePath ?? undefined, version: preview?.version },
            }),
          )
        : await deleteFile(target);
      if (!deleted) {
        throw new Error("File deletion did not succeed.");
      }
      if (lookupGenerationRef.current !== generation) {
        return;
      }
      await onDeleted(target);
      if (lookupGenerationRef.current === generation) {
        closeDialog();
      }
    } catch (error) {
      console.error("Failed to delete explorer item:", error);
      if (lookupGenerationRef.current === generation) {
        setIsDeleting(false);
        deletingRef.current = false;
        const { detail, title, messages } = extractProblemDetails(error);
        setDeleteError([...new Set([detail ?? title, ...messages].filter(Boolean))].join("\n") || `Failed to delete ${target.fileName}.`);
      }
    }
  }, [closeDialog, deleteFile, getUsage, onDeleted, target, projectId, usage, isLoadingUsage, usageLookupFailed, pathType, preview, requestHandler]);

  const deleteFileDialog = target ? (
    <DeleteFileDialog
      fileName={target.fileName}
      folder={target.folder}
      isDirectoryBundle={target.fileName.toLowerCase().endsWith(".d")}
      isFolder={target.isFolder}
      usedByJobs={usage?.jobs}
      canDelete={!(getUsage || target.isFolder) || (usage?.canDelete ?? false)}
      requiresWarning={usage?.requiresWarning ?? false}
      isLoadingUsage={isLoadingUsage}
      isDeleting={isDeleting}
      usageLookupFailed={usageLookupFailed}
      error={deleteError}
      files={preview?.files}
      onConfirm={() => void confirmDelete()}
      onCancel={closeDialog}
    />
  ) : null;

  return { deleteFileDialog, openDeleteDialog };
};

export type { DeleteTarget };
